export type AdminUserReport = {
  id: string;
  reporterId: string;
  reporterEmail: string | null;
  type: string;
  description: string;
  status: string;
  reviewNotes: string | null;
  actionTaken: string | null;
  createdAt: string;
  reviewedAt: string | null;
};

const REPORT_TYPE_LABELS: Record<string, string> = {
  inappropriate_behavior: "Inappropriate behavior",
  inappropriate_message: "Inappropriate message",
  harassment: "Harassment",
  fake_profile: "Fake profile",
  other: "Other",
};

const REPORT_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  investigating: "Investigating",
  resolved: "Resolved",
  dismissed: "Dismissed",
};

const ACTION_TAKEN_LABELS: Record<string, string> = {
  none: "No action",
  warning: "Warning sent",
  temporary_ban: "Suspended (temp)",
  permanent_ban: "Suspended",
  content_removed: "Content removed",
};

export function reportTypeLabel(type: string) {
  return REPORT_TYPE_LABELS[type] ?? type.replaceAll("_", " ");
}

export function reportStatusLabel(status: string) {
  return REPORT_STATUS_LABELS[status] ?? status;
}

export function reportActionTakenLabel(action: string | null) {
  if (!action) return "Resolved";
  return ACTION_TAKEN_LABELS[action] ?? action.replaceAll("_", " ");
}

export function reportOutcomeLabel(report: { status: string; actionTaken: string | null }) {
  if (report.status === "resolved" && report.actionTaken) {
    switch (report.actionTaken) {
      case "warning":
        return "Warning";
      case "temporary_ban":
        return "Suspended";
      case "permanent_ban":
        return "Suspended";
      case "none":
        return "No action";
      default:
        return reportActionTakenLabel(report.actionTaken);
    }
  }

  return reportStatusLabel(report.status);
}

export function reportOutcomeKind(report: { status: string; actionTaken: string | null }) {
  if (report.status === "dismissed" || report.actionTaken === "none") return "dismissed";
  if (report.actionTaken === "warning") return "warning";
  if (report.actionTaken === "temporary_ban") return "suspended";
  if (report.actionTaken === "permanent_ban") return "suspended-permanent";
  if (report.status === "pending") return "pending";
  if (report.status === "investigating") return "investigating";
  return "resolved";
}

export function formatModerationTimestamp(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function defaultSuspendNoteFromReports(reports: AdminUserReport[]) {
  const pending = reports.filter((report) => report.status === "pending");
  if (!pending.length) return "";

  return pending[0].description;
}
